import { Preference } from "../../pojo/PeferenceI";
import { PreferenceService } from "../../services/preference.service";
import { FileStoreUtil } from "../../util/filestore.util";
import { S25Util } from "../../util/s25-util";
import { PREF_25L_CONNECT25_BRANDING } from "./se.connect25.constants";

/**
 * Parsed + coalesced Connect25 theme from `PREF_25L_CONNECT25_BRANDING` JSON.
 * For reactive reads in Angular, inject `SeConnect25ThemeStore`.
 * `logoFileRef`: filestore query string or HTTPS URL only — never base64 in the pref.
 * Logo alt text lives in the filestore image payload, not in this JSON.
 * `supportEmail`: blank hides the Connect25 "Contact support" link.
 */
export type ThemeBranding = {
    productTitle: string;
    logoFileRef: string;
    bannerBackground: string;
    bannerAccent: string;
    bannerForeground: string;
    slideshowGradientStart: string;
    slideshowGradientEnd: string;
    supportEmail: string;
};

export const DEFAULT_THEME_BRANDING: ThemeBranding = {
    productTitle: "Connect25",
    logoFileRef: "",
    bannerBackground: "#46587c",
    bannerAccent: "#04345d",
    bannerForeground: "#ffffff",
    slideshowGradientStart: "#4a5568",
    slideshowGradientEnd: "#2d3748",
    supportEmail: "",
};

function isNonEmptyString(v: unknown): v is string {
    return typeof v === "string" && v.trim().length > 0;
}

function coerceHex(v: unknown, fallback: string): string {
    if (!isNonEmptyString(v)) return fallback;
    const s = v.trim();
    if (/^#[0-9A-Fa-f]{3,8}$/.test(s)) return s;
    return fallback;
}

/**
 * FileStore refs are query strings with `&`. Preference values are often carried in XML where a bare `&`
 * truncates or corrupts the stored JSON — encode for persistence and decode on read.
 */
function encodeLogoFileRefForPreference(ref: string): string {
    if (!ref) return "";
    if (ref.includes("://")) return ref;
    return encodeURIComponent(ref);
}

function decodeLogoFileRefFromPreference(raw: unknown): string {
    if (!isNonEmptyString(raw)) return "";
    const s = raw.trim();
    if (s.includes("://")) return s;
    try {
        const decoded = decodeURIComponent(s);
        if (FileStoreUtil.isFileStoreUrl(decoded)) return decoded;
    } catch {
        /* invalid escape sequences */
    }
    return FileStoreUtil.isFileStoreUrl(s) ? s : "";
}

function coerceEmail(v: unknown): string {
    if (!isNonEmptyString(v)) return "";
    const email = v.trim();
    return S25Util.isValidEmail(email) ? email : "";
}

export function parseThemeBrandingJson(raw: string | undefined | null): ThemeBranding {
    if (!raw?.trim()) return { ...DEFAULT_THEME_BRANDING };
    let obj: unknown;
    try {
        obj = JSON.parse(raw) as unknown;
    } catch {
        return { ...DEFAULT_THEME_BRANDING };
    }
    if (!obj || typeof obj !== "object") return { ...DEFAULT_THEME_BRANDING };
    const o = obj as Record<string, unknown>;
    return {
        productTitle: typeof o.productTitle === "string" ? o.productTitle.trim() : DEFAULT_THEME_BRANDING.productTitle,
        logoFileRef: decodeLogoFileRefFromPreference(o.logoFileRef),
        bannerBackground: coerceHex(o.bannerBackground, DEFAULT_THEME_BRANDING.bannerBackground),
        bannerAccent: coerceHex(o.bannerAccent, DEFAULT_THEME_BRANDING.bannerAccent),
        bannerForeground: coerceHex(o.bannerForeground, DEFAULT_THEME_BRANDING.bannerForeground),
        slideshowGradientStart: coerceHex(o.slideshowGradientStart, DEFAULT_THEME_BRANDING.slideshowGradientStart),
        slideshowGradientEnd: coerceHex(o.slideshowGradientEnd, DEFAULT_THEME_BRANDING.slideshowGradientEnd),
        supportEmail: coerceEmail(o.supportEmail),
    };
}

export function serializeThemeBranding(b: ThemeBranding): string {
    return JSON.stringify({
        productTitle: b.productTitle.trim(),
        logoFileRef: encodeLogoFileRefForPreference(b.logoFileRef.trim()),
        bannerBackground: b.bannerBackground,
        bannerAccent: b.bannerAccent,
        bannerForeground: b.bannerForeground,
        slideshowGradientStart: b.slideshowGradientStart,
        slideshowGradientEnd: b.slideshowGradientEnd,
        supportEmail: b.supportEmail.trim(),
    });
}

export async function loadThemeBrandingPreference(
    prefName: string,
    prefType: Preference.PrefType = "S",
): Promise<ThemeBranding> {
    try {
        const prefs = await PreferenceService.getPreferences([prefName], prefType);
        const raw = (prefs as Record<string, { value: unknown }> | undefined)?.[prefName]?.value as string | undefined;
        return parseThemeBrandingJson(raw);
    } catch {
        return { ...DEFAULT_THEME_BRANDING };
    }
}

/** Loads canonical Connect25 branding system pref. */
export async function loadConnect25Branding(): Promise<ThemeBranding> {
    return loadThemeBrandingPreference(PREF_25L_CONNECT25_BRANDING, "S");
}
